/**
 * @author jakubbujakowski
 * @since 2020-10-29
 */

import * as React from "react";
import styled from "styled-components";
import ReactSlider, { ReactSliderProps } from "react-slider";
import { Element as ScrollElement } from "react-scroll";

import { SLIDER } from "@config/testIds/slider";
import {
  IS_BROWSER,
  LONG_DEBOUNCE,
  SCROLL_ELEMENT_SLIDER,
} from "@config/consts";

import { Button, Tooltip, TestWrapper } from "@ui/Atoms";
import { rem, transition } from "@ui/helpers";

import { useBreakpoint } from "@hooks/useBreakpoint";
import { useDebouncedEffect } from "@hooks/useDebouncedEffect";

interface Props extends ReactSliderProps {
  isDisabled?: boolean;
  withButtons?: boolean;
  step?: number;
  max: number;
  min: number;
  onChange: (val: number) => void;
  renderLabel: (val: number) => string;
  renderTooltip?: (val: number) => string;
}

const Slider = (props: Props): React.ReactElement => {
  const { mobile, tablet, desktop, desktopLarge } = useBreakpoint("v1");

  /**
   * It can happen that value passed to this function is lower than the allowed minimum value
   * In that case props.min should be returned instead
   */
  const resolveValue = (val: number | number[] | undefined) => {
    const resolvedVal = Array.isArray(val) ? Number(val[0]) : Number(val);
    return resolvedVal < props.min ? props.min : resolvedVal;
  };

  const sliderStep = props.step || 1;

  const [sliderValue, setSliderValue] = React.useState<number>(
    () => resolveValue(props.value) || 0,
  );
  const [isDragging, setIsDragging] = React.useState<boolean>(false);
  const [tooltipVisible, setTooltipVisible] = React.useState<boolean>(false);

  const tooltipText = props.renderTooltip && props.renderTooltip(sliderValue);
  const labelText = props.renderLabel(sliderValue);

  // fixes react-slider bug, where slider would lose styling after breakpoint change
  React.useEffect(() => {
    if (IS_BROWSER) {
      window.dispatchEvent(new Event("resize"));
    }
  }, [mobile, tablet, desktop, desktopLarge]);

  React.useEffect(() => {
    setSliderValue(resolveValue(props.value));
  }, [props.value]);

  useDebouncedEffect(() => props.onChange(sliderValue), LONG_DEBOUNCE, [
    sliderValue,
  ]);

  return (
    <TestWrapper testId={SLIDER.WRAPPER}>
      <SliderWrapper
        withTooltip={Boolean(props.renderTooltip)}
        isDisabled={props.isDisabled}
      >
        <TestWrapper testId={SLIDER.BUTTON_LEFT}>
          <SliderButtonLeft
            type="button"
            variant="borderGrey"
            size="icon"
            auto
            onClick={() => {
              const newValue = sliderValue - sliderStep;

              if (props.isDisabled) {
                return;
              }

              if (newValue >= props.min) {
                setSliderValue(newValue);
              }
            }}
          >
            {" - "}
          </SliderButtonLeft>
        </TestWrapper>
        <ScrollElement name={SCROLL_ELEMENT_SLIDER} />
        <StyledSlider
          {...props}
          disabled={props.isDisabled}
          value={sliderValue}
          renderThumb={(renderProps, renderState) => (
            <TestWrapper testId={SLIDER.THUMB} key={renderState.index}>
              <ThumbWrapper {...(renderProps as any)}>
                {tooltipText && (
                  <Tooltip
                    placeholder={String(tooltipText)}
                    disabled={isDragging}
                    offset={{ top: 20 }}
                    delayShow={250}
                    {...(tooltipVisible && { isVisible: true })}
                  >
                    <ThumbContent>
                      <StyledThumb
                        labelText={labelText}
                        isDragging={isDragging}
                      />
                      <ThumbBg />
                    </ThumbContent>
                  </Tooltip>
                )}
              </ThumbWrapper>
            </TestWrapper>
          )}
          renderTrack={(props, state) => (
            <StyledTrack {...(props as any)} index={state.index} />
          )}
          onChange={val => {
            const currentValue = Array.isArray(val) ? val[0] : val;
            if (
              currentValue &&
              currentValue >= props.min &&
              currentValue <= props.max
            ) {
              setSliderValue(currentValue);
            }
          }}
          onBeforeChange={() => {
            setIsDragging(true);
            setTooltipVisible(false);
          }}
          onAfterChange={() => {
            setIsDragging(false);
            setTooltipVisible(true);
          }}
        />
        <TestWrapper testId={SLIDER.BUTTON_RIGHT}>
          <SliderButtonRight
            type="button"
            variant="borderGrey"
            size="icon"
            auto
            onClick={() => {
              const newValue = sliderValue + sliderStep;

              if (props.isDisabled) {
                return;
              }

              if (newValue <= props.max) {
                setSliderValue(newValue);
              }
            }}
          >
            {" + "}
          </SliderButtonRight>
        </TestWrapper>
      </SliderWrapper>
    </TestWrapper>
  );
};

export { Slider };

Slider.displayName = "Slider";

const SliderWrapper = styled.div<{
  withTooltip?: boolean;
  isDisabled?: boolean;
}>`
  display: flex;
  align-items: center;
  margin: ${props =>
    props.withTooltip
      ? `${props.theme.margins.base_x10} 0 ${props.theme.margins.base_x4}`
      : `${props.theme.margins.base_x4} 0`};
  padding: ${props => props.theme.margins.base_x2} 0;

  ${props =>
    props.isDisabled &&
    `
    opacity: 0.5;
    pointer-events: none;
  `}
`;

const StyledSlider = styled(ReactSlider)`
  width: 90%;
  height: ${props => props.theme.margins.half};
`;

const ThumbWrapper = styled.div`
  outline: none;
  height: 0;
  margin-top: ${rem(2)};
`;

const StyledThumb = styled.div<{ labelText: string; isDragging: boolean }>`
  position: relative;
  height: ${props => props.theme.margins.base_x3};
  width: ${props => props.theme.margins.base_x3};
  text-align: center;
  background-color: ${props => props.theme.palette.green500};
  color: ${props => props.theme.palette.white};
  border-radius: 50%;
  cursor: grab;
  top: 50%;
  transform: translateY(-50%);

  &:after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${props => props.theme.margins.base_x2};
    height: ${props => props.theme.margins.base_x2};
    background: ${props => props.theme.palette.green700};
    border-radius: 50%;
    transform: translate(-50%, -50%);
  }

  &:before {
    content: "${props => props.labelText}";
    position: absolute;
    top: 0;
    color: ${props => props.theme.palette.grey800};
    font-size: ${props => props.theme.fonts.sizes.caption};
    min-width: ${rem(120)};
    transform: translate(-50%, -${props => props.theme.margins.base_x5});
    opacity: 1;
    pointer-events: none;
    transition: ${transition(["top", "opacity"])};
  }

  ${props =>
    !props.isDragging &&
    `
  &:hover {
    &:before {
      opacity: 0;
      top: -${props.theme.margins.base};
      transition: ${transition(["top", "opacity"])};
    }
  }
  
  `}
`;

const StyledTrack = styled.div<{ index: number }>`
  top: 0;
  bottom: 0;
  background: ${props =>
    props.index === 1
      ? props.theme.palette.grey100
      : props.theme.palette.green500};
  border-radius: ${rem(8)};
`;

const SliderButton = styled(Button)`
  border: none;
  font-weight: ${props => props.theme.fonts.weights.light};
  font-size: ${props => props.theme.fonts.sizes.h3};
  min-height: ${props => props.theme.buttons.sizes.pill};
  color: ${props => props.theme.palette.blue500};
  line-height: 0;

  &:hover,
  &:active,
  &:focus {
    color: ${props => props.theme.palette.blue700};
    border: none;
  }
`;

const SliderButtonLeft = styled(SliderButton)`
  margin-right: ${props => props.theme.margins.base_x2};
  transform: translateY(-${props => props.theme.margins.half});
`;

const SliderButtonRight = styled(SliderButton)`
  margin-left: ${props => props.theme.margins.base_x2};
`;

const ThumbBg = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${props => props.theme.margins.base_x3};
  height: ${props => props.theme.margins.base_x3};
  background: ${props => props.theme.palette.green300};
  border-radius: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.5;
  z-index: -1;
  pointer-events: none;
  transition: ${transition(["width", "height", "transform"])};
`;

const ThumbContent = styled.div`
  height: 0;

  &:hover {
    ${ThumbBg} {
      width: ${props => props.theme.margins.base_x6};
      height: ${props => props.theme.margins.base_x6};
      transform: translate(-50%, -50%);
      transition: ${transition(["width", "height", "transform"])};
    }
  }
`;
